import * as React from "react";
import {cs} from "../../../../../../../../../react/chain-services";
import {consumeContext} from "../../../../../../../../../react/context";
import "./routes-table.scss";
import {Responsive}     from "@Common/ui-components/responsive";
import {DataTableD1} from "../../../../../../../common/design2/data-table/data-table-d1";
import {SearchInput} from "../../../../../../../common/design2/search-input/search-input";
import {plural} from "../../../../../../../../language/plural";
import {UseState} from "../../../../../../../../../react/use-state";
import {Checkbox} from "../../../../../../../common/design2/checkbox/checkbox";
import {getPageSummary}         from "@Common/ui-components/heavy/common/design2/data-table/get-page-summary";
import {PageList} from "../../../../../../../common/design2/pagination-ui/page-list/page-list";
import {PageSize} from "../../../../../../../common/design2/pagination-ui/page-size/page-size";
import {equalDeep} from "../../../../../../../../../utils/objects";
import {chain} from "../../../../../../../../../utils/fs";
import {formatNumberBig} from "../../../../../../../../language/format-number-big";
import {sum} from "../../../../../../../../../utils/collections";
import {InformationBadge} from "heavy/common/design2/information-badge/information-badge";

export const RoutesTable = ({allRoutes, next}) => cs(
    consumeContext("company"),
    // @ts-ignore
    ["responsive", ({}, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ["unselecteds", (_, next) => UseState({next})],
    ["sortState", (_, next) => UseState({next})],
    ["filter", (_, next) => UseState({
        next,
    })],
    ["filteredRecords", ({filter}, next) => next(!filter.value || !allRoutes ? (
        allRoutes
    ) : chain(
        filter.value.toLowerCase(),
        (_) => allRoutes.filter((r) => (""+r.zip_code).toLowerCase().includes(_) || (""+r.route).toLowerCase().includes(_))
    ))],
    ["pageSize", (_, next) => UseState({initValue: 10, next})],
    ({filter, filteredRecords, unselecteds, sortState, pageSize, responsive, company}) => {
        const toKey = (route) => ({
            route: route.route,
            zip_code: route.zip_code,
        });

        const matchKey = (key, route) => key.route === route.route && key.zip_code === route.zip_code;
        const hasSortPriority = (r) => !sortState.value?.some((r1) => matchKey(r1, r));
        const sortRows = (r1, r2) => (
            (hasSortPriority(r1) && !hasSortPriority(r2)) || //sort selected rows first
            (r1.match_rate && !r2.match_rate) || //sort rows that have match rate over rows that don't
            r1.match_rate > r2.match_rate //finally sort by match rate
        ) ? -1 : 0;
        const isSelected = (r) => !unselecteds.value?.some((r1) => matchKey(r1, r));
        const getSelecteds = () => allRoutes.filter(isSelected).reduce((selecteds, r) => ({
            ...selecteds,
            [r.zip_code+r.route]: true,
        }), {});

        const selectedRecords = filteredRecords?.filter(isSelected);
        const totalCount = sum(allRoutes, (r) => r.count);
        const selectedCount = sum(selectedRecords, (r) => r.count);

        return next({
            valid: !unselecteds.value || unselecteds.value.length < allRoutes.length,
            totalCount,
            selectedCount,
            getSelecteds,
            reset: () => unselecteds.onChange(null),
            setUnselecteds: ({unselectedRoutes}) => {
                unselecteds.onChange(unselectedRoutes);
                sortState.onChange(unselectedRoutes);
            },
            getSubmitData: () => allRoutes.map((r) => ({
                ...r,
                display_eddm_route: isSelected(r) ? 1 : 0,
            })),
            renderSearch: () => (
                <div className="routes-table-8ys search">
                    {SearchInput({
                        state: filter,
                        placeholder: "Search",
                        text: filter.value && <>Found <b>{filteredRecords.length}</b> {plural("route", filteredRecords.length)}</>,
                        xdQaName: "Neighborhood Search Routes Table Search",
                    })}
                </div>
            ),
            renderTable: () => (
                <div className="routes-table-8ys table">
                    {DataTableD1({
                        pageSize: pageSize.value,
                        columns: Columns({
                            masterCheckbox: (() => {
                                const deselectAll = () => unselecteds.change((_) => [
                                    ..._ ?? [],
                                    ...filteredRecords.filter((r) => isSelected(r)).map(toKey),
                                ]);
                                const selectAll = () => unselecteds.change((_) => _.filter((key) => !filteredRecords.find((r) => equalDeep(toKey(r), key))));
                                return (
                                    !filteredRecords.find(isSelected) ? {
                                        checked: false,
                                        onClick: selectAll,
                                    } : filteredRecords.find((r) => !isSelected(r)) ? {
                                        checked: "midway",
                                        onClick: selectAll,
                                    } : {
                                        checked: true,
                                        onClick: deselectAll,
                                    }
                                );
                            })(),
                        }),
                        darkRows: "even",
                        rows: filteredRecords?.toSorted(sortRows).map((route) => {
                            const checked = isSelected(route);
                            return ({
                                columns: [
                                    {
                                        render: () => Checkbox({
                                            checked,
                                            onClick: checked ? (
                                                () => unselecteds.change((_) => [
                                                    ..._ ?? [],
                                                    toKey(route),
                                                ])
                                            ) : (
                                                () => unselecteds.change((_) => _.filter((r1) => !matchKey(r1, route)))
                                            ),
                                            xdQaName: "Neighborhood Search "+route.zip_code+" "+route.route,
                                        }),
                                    },
                                    {
                                        render: () => route.zip_code,
                                        sortValue: () => ("" + route.zip_code).toLowerCase(),
                                        domAttrs: {
                                            "xd-qa-type": "label",
                                            "xd-qa-name": "Neighborhood Search "+route.zip_code+" "+route.route+" Zip Code",
                                        },
                                    },
                                    {
                                        render: () => route.route,
                                        sortValue: () => route.route.toLowerCase(),
                                        domAttrs: {
                                            "xd-qa-type": "label",
                                            "xd-qa-name": "Neighborhood Search "+route.zip_code+" "+route.route,
                                        },
                                    },
                                    {
                                        render: () => route.count,
                                        sortValue: () => +route.count,
                                        domAttrs: {
                                            "xd-qa-type": "label",
                                            "xd-qa-name": "Neighborhood Search "+route.zip_code+" "+route.route+" Records",
                                        },
                                    },
                                    {
                                        render: () => route.match_rate !== undefined ? route.match_rate + "%" : "--",
                                        sortValue: () => route.match_rate !== undefined ? +route.match_rate : -1,
                                        domAttrs: {
                                            "xd-qa-type": "label",
                                            "xd-qa-name": "Neighborhood Search "+route.zip_code+" "+route.route+" Score",
                                        },
                                    },
                                ].filter(v=>v),
                            });
                        }) ?? [],
                        emptyAction: {
                            label: "No routes found",
                        },
                        getFooter: ({colCount, pagination}) => ({
                            columns: [{
                                colSpan: colCount,
                                render: responsive.ge("md") ? () => (
                                    <div className="controls">
                                        {filteredRecords?.length > 10 ? (
                                            PageSize({
                                                state: pageSize,
                                                xdQaNamePrefix: "Neighborhood Search Routes Table",
                                            })
                                        ) : (
                                            <div className="spacer"/>
                                        )}

                                        {pagination.active && PageList({
                                            ...pagination,
                                            xdQaNamePrefix: "Neighborhood Search Routes Table",
                                        })}

                                        <div className="summary">
                                            <table>
                                                <tbody>
                                                <tr className="total">
                                                    <td className="label" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records">
                                                        Total Records
                                                    </td>
                                                    <td className="value" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Quantity">
                                                        {formatNumberBig(totalCount)}
                                                    </td>
                                                </tr>
                                                <tr className="selected">
                                                    <td className="label" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Selected">
                                                        Total Records Selected
                                                    </td>
                                                    <td className="value" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Selected Quantity">
                                                        {formatNumberBig(sum(selectedRecords, (r) => r.count))}
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : () => (
                                    <>
                                        <div className="controls">
                                            {filteredRecords?.length > 10 ? (
                                                PageSize({
                                                    state: pageSize,
                                                    pageSizeMessage: "",
                                                    xdQaNamePrefix: "Neighborhood Search Routes Table",
                                                })
                                            ) : (
                                                <div className="spacer"/>
                                            )}

                                            {pagination.active && PageList({
                                                ...pagination,
                                                xdQaNamePrefix: "Neighborhood Search Routes Table",
                                            })}                                               
                                        </div>
                                        <div className="summary">
                                            {getPageSummary({
                                                pageIndex: pagination.state.value,
                                                pageSize: pageSize.value,
                                                count: filteredRecords?.length
                                            })}
                                        </div>
                                        <div className="results">
                                            <div className="total">
                                                <div className="label" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records">
                                                    Total Records
                                                </div>
                                                <td className="value" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Quantity">
                                                    {formatNumberBig(totalCount)}
                                                </td>
                                            </div>
                                            <div className="selected">
                                                <div className="label" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Selected">
                                                    Total Records Selected
                                                </div>
                                                <div className="value" xd-qa-type="label" xd-qa-name="Neighborhood Search Total Records Selected Quantity">
                                                    {formatNumberBig(sum(selectedRecords, (r) => r.count))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )                                
                            }],
                        }),
                    })}
                </div>
            ),
        });
    },
);

const Columns = ({masterCheckbox}) => [
    {
        width: "1%",
        className: "checkbox",
        render: () => Checkbox({
            ...masterCheckbox,
            xdQaName: "Neighborhood Search Routes Table Master Checkbox",
        }),
    },
    {
        width: "10%",
        label: "ZIP CODE",
        type: "text",
        className: "zip",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Neighborhood Search Routes Table Zip Code",
        }
    },
    {
        width: "1%",
        label: "NEIGHBORHOOD",
        type: "text",
        className: "carrier-routes",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Neighborhood Search Routes Table Carrier Route",
        }
    },
    {
        width: "10%",
        label: "RECORDS",
        type: "text",
        className: "records",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Neighborhood Search Routes Table Records",
        }
    },
    {
        render: () => <>
            SCORE {InformationBadge({
                height: 18,
                getTooltip: () => (
                    <div className="message-5ww">
                        <p>This percentage represents the proportion of residences within the neighborhood who meet the criteria specified in your search.</p>
                        <p>Note: We will mail to the entire neighborhood route to ensure comprehensive coverage.</p>
                    </div>
                ),
                direction: "south-west",
                xdQaName: "Neighborhood Search Routes Table Score Info Badge",
            })}
        </>,
        type: "text",
        className: "match-rate",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Neighborhood Search Routes Table Score",
        }
    },
].filter(v=>v);